import React, { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";

import InputField from "../../components/ui/InputField";

const RoleForm = ({ role, permissions, onClose, onSaved }) => {
  const [name, setName] = useState(role?.name || "");
  const [selected, setSelected] = useState(role?.permissions || []);
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/roles";

  const grouped = permissions.reduce((acc, p) => {
    acc[p.modulo] = acc[p.modulo] || [];
    acc[p.modulo].push(p);
    return acc;
  }, {});

  const togglePermission = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("El nombre del rol es obligatorio");
      return;
    }

    try {
      setIsLoading(true);
      setSaving(true);

      if (role) {
        await axios.put(
          `${API_URL}/${role._id}`,
          { name, permissions: selected },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Rol actualizado ✅");
      } else {
        await axios.post(
          API_URL,
          { name, permissions: selected },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Rol creado ✅");
      }

      onClose();
      onSaved();
    } catch (err) {
      setIsLoading(false);
      toast.error("Error al guardar rol");
    } finally {
      setIsLoading(false);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      {/* Modal */}
      <div className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">
            {role ? "Editar rol" : "Crear rol"}
          </h3>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full p-1 transition"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scroll interno */}
        <div className="flex-1 overflow-y-auto">
          <Card className="border-none shadow-none">
            <CardContent className="p-6">
              {/* Nombre */}
              <InputField
                label="Nombre del rol"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={role?.sistema}
              />

              {/* Permisos */}
              <div className="mt-6 space-y-4">
                {Object.entries(grouped).map(([modulo, perms]) => (
                  <div key={modulo}>
                    <h4 className="font-semibold text-sm mb-2">{modulo}</h4>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {perms.map((p) => (
                        <label
                          key={p.key}
                          className="flex items-start gap-2 text-sm cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(p.key)}
                            onChange={() => togglePermission(p.key)}
                            disabled={role?.sistema}
                            className="mt-1"
                          />
                          <span>
                            <strong>{p.key}</strong>
                            <br />
                            <span className="text-xs text-gray-500">
                              {p.descripcion}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {isLoading ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;
